import PropTypes from 'prop-types';
import { X, RotateCcw } from 'lucide-react';
import { useVenuesStore } from '@/features/venues/store/useVenuesStore';
import { Stack, Text, Heading } from '@/components/ui/index.js';
import styles from './FilterPanel.module.css';

/**
 * FilterPanel - Slide-out filter panel for venue filtering
 * 
 * Features sport type filters, amenity filters, distance radius,
 * open now toggle, and rating minimum filter.
 */
export function FilterPanel({ isOpen, onClose }) {
  const {
    filters,
    toggleSportType,
    toggleAmenity,
    setMaxDistance,
    toggleOpenNow,
    setMinRating,
    clearFilters,
    getAvailableSports,
    getAvailableAmenities,
    getSportIcon,
  } = useVenuesStore();

  const availableSports = getAvailableSports();
  const availableAmenities = getAvailableAmenities();

  const hasActiveFilters = 
    filters.sportTypes.length > 0 ||
    filters.amenities.length > 0 ||
    filters.maxDistance !== 10 ||
    filters.openNow ||
    filters.minRating > 0;

  return (
    <>
      {/* Overlay */}
      <div 
        className={`${styles.overlay} ${isOpen ? styles.overlayVisible : ''}`}
        onClick={onClose}
        aria-hidden={!isOpen}
      />
      
      {/* Panel */}
      <aside 
        className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Filter venues"
      >
        {/* Header */}
        <div className={styles.header}>
          <Heading level={3} size="lg" color="primary">Filters</Heading>
          <button 
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close filters"
          >
            <X size={24} />
          </button>
        </div>

        <div className={styles.content}>
          <Stack direction="column" gap={6}>
            {/* Sport Type Filter */}
            <section className={styles.filterSection}>
              <Text size="sm" weight="semibold" color="secondary" className={styles.sectionTitle}>
                Sport Type
              </Text>
              <div className={styles.chipGrid}>
                {availableSports.map(sport => (
                  <button
                    key={sport}
                    className={`${styles.chip} ${filters.sportTypes.includes(sport) ? styles.chipActive : ''}`}
                    onClick={() => toggleSportType(sport)}
                  >
                    <span className={styles.chipIcon}>{getSportIcon(sport)}</span>
                    <span className={styles.chipLabel}>{sport}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* Distance Filter */}
            <section className={styles.filterSection}>
              <Text size="sm" weight="semibold" color="secondary" className={styles.sectionTitle}>
                Max Distance
              </Text>
              <div className={styles.sliderContainer}>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="0.5"
                  value={filters.maxDistance}
                  onChange={(e) => setMaxDistance(parseFloat(e.target.value))}
                  className={styles.slider}
                />
                <Text size="sm" color="primary" className={styles.sliderValue}>
                  Within {filters.maxDistance} miles
                </Text>
              </div>
            </section>

            {/* Open Now Toggle */}
            <section className={styles.filterSection}>
              <label className={styles.toggleRow}>
                <Text size="sm" weight="semibold" color="secondary">
                  Open Now Only
                </Text>
                <button
                  className={`${styles.toggle} ${filters.openNow ? styles.toggleActive : ''}`}
                  onClick={toggleOpenNow}
                  role="switch"
                  aria-checked={filters.openNow}
                >
                  <span className={styles.toggleThumb} />
                </button>
              </label>
            </section>

            {/* Rating Filter */}
            <section className={styles.filterSection}>
              <Text size="sm" weight="semibold" color="secondary" className={styles.sectionTitle}>
                Minimum Rating
              </Text>
              <div className={styles.ratingButtons}>
                {[0, 3, 3.5, 4, 4.5].map(rating => (
                  <button
                    key={rating}
                    className={`${styles.ratingButton} ${filters.minRating === rating ? styles.ratingButtonActive : ''}`}
                    onClick={() => setMinRating(rating)}
                  >
                    {rating === 0 ? 'Any' : `${rating}+ ★`}
                  </button>
                ))}
              </div>
            </section>

            {/* Amenities Filter */}
            <section className={styles.filterSection}>
              <Text size="sm" weight="semibold" color="secondary" className={styles.sectionTitle}>
                Amenities
              </Text>
              <div className={styles.checkboxList}>
                {availableAmenities.map(amenity => (
                  <label key={amenity} className={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={filters.amenities.includes(amenity)}
                      onChange={() => toggleAmenity(amenity)}
                      className={styles.checkbox}
                    />
                    <span className={styles.checkmark} />
                    <Text size="sm" color="primary">{amenity}</Text>
                  </label>
                ))}
              </div>
            </section>
          </Stack>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button 
            className={`${styles.clearButton} ${hasActiveFilters ? styles.clearButtonVisible : ''}`}
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            <RotateCcw size={16} />
            Clear All
          </button>
          <button 
            className={styles.applyButton}
            onClick={onClose}
          >
            Show Results
          </button>
        </div>
      </aside>
    </>
  );
}

FilterPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FilterPanel;
